import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { type AgentDashboardSnapshot, toAgentDashboardSnapshot } from '@/chat/lib/agent-dashboard-snapshot';
import type { DashboardSpec } from '@/dashboard/types';

export interface DashboardArtifactSnapshot {
  title: string;
  slug: string;
  warehouseId: string;
  description?: string | null;
  spec: DashboardSpec;
}

export interface SavedDashboardRef {
  id: string;
  slug: string;
}

export interface DashboardArtifact extends DashboardArtifactSnapshot {
  saved: SavedDashboardRef | null;
  /** Session-monotonic revision counter (not raw SSE patchSeq — that resets each turn). */
  patchSeq: number;
  /** Last revision persisted to the workspace (manual or auto-save). */
  lastPersistedPatchSeq: number;
  /** Set when dashboard.get returns NOT_FOUND — row was deleted outside this chat. */
  externallyRemoved?: boolean;
  /**
   * Session patchSeq of the in-flight `update_dashboard` server persist, or `undefined` when none.
   * Single source of truth for "a server persist is in flight" (was previously mirrored by a separate
   * `serverPersistPending` boolean; the two could drift, which wedged auto-save on abort). While set,
   * the panel stays on the preview path and client auto-save is gated. Read via `isArtifactSaving`.
   */
  pendingPersistPatchSeq?: number;
}

/** True while an `update_dashboard` server persist is in flight (panel stays on the preview path). */
export function isArtifactSaving(artifact: DashboardArtifact): boolean {
  return artifact.pendingPersistPatchSeq !== undefined;
}

/** True when the artifact's latest spec has been persisted (manual or auto-save). */
export function isArtifactPersisted(artifact: DashboardArtifact): boolean {
  return artifact.saved !== null && !isArtifactSaving(artifact) && artifact.patchSeq <= artifact.lastPersistedPatchSeq;
}

/** True when the artifact has unpersisted local edits that client auto-save should flush. */
export function isArtifactAutoSaveEligible(
  artifact: DashboardArtifact,
): artifact is DashboardArtifact & { saved: SavedDashboardRef } {
  return !!artifact.saved?.id && !isArtifactSaving(artifact) && artifact.patchSeq > artifact.lastPersistedPatchSeq;
}

/** True for unsaved previews in transcript cards and the Artifacts menu (not saved, not not-found). */
export function isArtifactDraftForDisplay(artifact: DashboardArtifact): boolean {
  return artifact.saved === null && !artifact.externallyRemoved;
}

/** True when a not-found dashboard still has an in-memory spec to restore as a draft. */
export function canRestoreDashboardDraft(artifact: DashboardArtifact): boolean {
  return artifact.spec.panels.length > 0;
}

export interface ChatDashboardArtifactsStore {
  artifacts: Record<string, DashboardArtifact>;
  artifactList: DashboardArtifact[];
  activeSlug: string | null;
  activeArtifact: DashboardArtifact | null;
  panelOpen: boolean;
  closePanel: () => void;
  discard: () => void;
  applyAgentDraft: (payload: Record<string, unknown>) => void;
  setActive: (slug: string, options?: { openPanel?: boolean }) => void;
  markSaved: (slug: string, saved: SavedDashboardRef) => void;
  markAutoSaved: (slug: string, patchSeq: number) => void;
  /**
   * Ack a successful save/update. Pass `savedPatchSeq` (the session revision that was
   * actually persisted) so a newer in-flight draft is kept and only that seq is marked
   * persisted — matching `markAutoSaved` race safety.
   */
  syncAfterSave: (
    slug: string,
    snapshot: DashboardArtifactSnapshot,
    saved: SavedDashboardRef,
    savedPatchSeq?: number,
  ) => void;
  syncFromTranscript: (snapshot: DashboardArtifactSnapshot) => void;
  /** Sync title + description from DB while saved+synced (panel open). */
  syncMetaFromDb: (slug: string, meta: { title: string; description: string | null }) => void;
  /** Clear stuck update_dashboard pending so client auto-save can resume. */
  clearServerPersistPending: (slug: string) => void;
  /**
   * Terminal abort/disconnect: clear the in-flight `pendingPersistPatchSeq` on every artifact. The turn
   * is over, so no late `update_dashboard` tool_result will adopt — leaving it set would gate client
   * auto-save (and stick the "Saving…" badge) for the rest of the session. If a result does arrive late,
   * `syncAfterSave` still keeps the newer draft with no flushed seq, so recovery is a safe redundant
   * free save rather than a wedged artifact.
   */
  clearAllServerPersistPending: () => void;
  /**
   * Re-apply in-flight persist markers that an OPTIMISTIC stop cleared, when the stop RPC then failed and
   * the turn is actually still running (so the `update_dashboard` persist is still in flight). Guarded:
   * only restores a slug still cleared AND still dirty-on-a-saved-artifact, so it can't re-gate (stick
   * "Saving…") an update that already adopted in the meantime.
   */
  restoreServerPersistPending: (pending: Record<string, number>) => void;
  markSavedRemoved: (slug: string) => void;
  /** Re-open a not-found dashboard's cached spec as an unsaved draft (no DB write). */
  restoreDraftFromMemory: (slug: string) => void;
  /** Create a minimal saved artifact entry and open the panel (historical cards without store). */
  hydrateSavedAndOpen: (slug: string, hint: { title: string; dashboardId?: string }) => void;
  /** Open panel in not-found state (keep cached spec for Restore when present). */
  hydrateNotFoundAndOpen: (slug: string, hint: { title: string }) => void;
  restoreFromHistory: (entries: Record<string, DashboardArtifact>, options?: { hasActiveTurn?: boolean }) => void;
  snapshot: () => AgentDashboardSnapshot | undefined;
  inventorySnapshot: () => { slug: string; title: string; saved: boolean; dashboardId?: string }[];
}

/** True when hydrate left an empty shell (no warehouse / panels) — lose restore ties to history. */
export function isArtifactContentEmpty(artifact: Pick<DashboardArtifactSnapshot, 'warehouseId' | 'spec'>): boolean {
  return !artifact.warehouseId && artifact.spec.panels.length === 0;
}

function sameSavedRef(a: SavedDashboardRef | null, b: SavedDashboardRef): boolean {
  return a !== null && a.id === b.id && a.slug === b.slug;
}

/** True when an artifact already holds the snapshot's exact content (title/description/warehouse/spec). */
function isSameDraftContent(artifact: DashboardArtifact, snapshot: DashboardArtifactSnapshot): boolean {
  return (
    artifact.title === snapshot.title &&
    artifact.warehouseId === snapshot.warehouseId &&
    (artifact.description ?? null) === (snapshot.description ?? null) &&
    JSON.stringify(artifact.spec) === JSON.stringify(snapshot.spec)
  );
}

function resolveSavedRefForAgentDraft(
  payload: Record<string, unknown>,
  existing: DashboardArtifact | undefined,
  slug: string,
): SavedDashboardRef | null {
  const origin = payload['origin'];
  const dashboardId =
    typeof payload['dashboardId'] === 'string' && payload['dashboardId'].length > 0 ? payload['dashboardId'] : null;

  if (origin === 'preview') return null;
  if (origin === 'update' && dashboardId) return { id: dashboardId, slug };
  if (existing?.saved?.slug === slug && existing.saved.id) return existing.saved;
  return null;
}

function snapshotToArtifact(
  snapshot: DashboardArtifactSnapshot,
  saved: SavedDashboardRef | null,
  patchSeq = 0,
  lastPersistedPatchSeq = 0,
): DashboardArtifact {
  return {
    ...snapshot,
    saved,
    patchSeq,
    lastPersistedPatchSeq,
  };
}

/** Bump session revision; SSE patchSeq is only used for within-turn dedup. */
function nextSessionPatchSeq(existing: DashboardArtifact | undefined): number {
  return (existing?.patchSeq ?? -1) + 1;
}

export function useChatDashboardArtifacts(): ChatDashboardArtifactsStore {
  const [artifacts, setArtifacts] = useState<Record<string, DashboardArtifact>>({});
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const artifactsRef = useRef(artifacts);
  const activeSlugRef = useRef(activeSlug);
  useLayoutEffect(() => {
    artifactsRef.current = artifacts;
  }, [artifacts]);
  useLayoutEffect(() => {
    activeSlugRef.current = activeSlug;
  }, [activeSlug]);
  const lastPatchTurnRef = useRef<string | null>(null);
  const lastPatchSeqBySlugRef = useRef(new Map<string, number>());
  const dismissedRef = useRef(false);

  const commitArtifacts = useCallback(
    (updater: (prev: Record<string, DashboardArtifact>) => Record<string, DashboardArtifact>) => {
      setArtifacts((prev) => {
        const next = updater(prev);
        artifactsRef.current = next;
        return next;
      });
    },
    [],
  );

  const artifactList = useMemo(() => Object.values(artifacts), [artifacts]);
  const activeArtifact = activeSlug ? (artifacts[activeSlug] ?? null) : null;

  const closePanel = useCallback(() => {
    dismissedRef.current = true;
    setPanelOpen(false);
  }, []);

  const discard = useCallback(() => {
    artifactsRef.current = {};
    setArtifacts({});
    setActiveSlug(null);
    setPanelOpen(false);
    dismissedRef.current = false;
    lastPatchSeqBySlugRef.current = new Map();
    lastPatchTurnRef.current = null;
  }, []);

  const applyAgentDraft = useCallback(
    (payload: Record<string, unknown>) => {
      const turnId = typeof payload['turnId'] === 'string' ? payload['turnId'] : null;
      const patchSeq = typeof payload['patchSeq'] === 'number' ? payload['patchSeq'] : null;
      const spec = payload['spec'];
      const title = payload['title'];
      const slug = payload['slug'];
      const warehouseId = payload['warehouseId'];
      if (typeof title !== 'string' || typeof slug !== 'string' || typeof warehouseId !== 'string') return;
      if (!spec || typeof spec !== 'object') return;

      const isNewTurn = turnId !== lastPatchTurnRef.current;
      if (isNewTurn) {
        lastPatchTurnRef.current = turnId;
        lastPatchSeqBySlugRef.current = new Map();
      }

      const lastSeq = lastPatchSeqBySlugRef.current.get(slug) ?? -1;
      if (patchSeq !== null && patchSeq <= lastSeq) return;
      if (patchSeq !== null) lastPatchSeqBySlugRef.current.set(slug, patchSeq);

      commitArtifacts((prev) => {
        const existing = prev[slug];
        const nextPatchSeq = nextSessionPatchSeq(existing);
        const savedRef = resolveSavedRefForAgentDraft(payload, existing, slug);
        const origin = payload['origin'];
        const isUpdatePersist = origin === 'update' && savedRef !== null;
        let pendingPersistPatchSeq: number | undefined;
        if (isUpdatePersist) {
          pendingPersistPatchSeq = nextPatchSeq;
        } else if (existing?.pendingPersistPatchSeq !== undefined) {
          // Keep an in-flight update_dashboard persist gated across intermediate drafts — INCLUDING a
          // preview-origin draft that lands before the update's tool_result. Clearing it here (the old
          // `origin === 'preview'` branch did) would ungate client auto-save while the server persist is
          // still in flight, racing a parallel dashboard.save against the update. syncAfterSave clears it
          // once the update's result adopts.
          pendingPersistPatchSeq = existing.pendingPersistPatchSeq;
        }
        // else (preview with no in-flight update, or any origin with no pending): undefined — not saving.
        return {
          ...prev,
          [slug]: {
            title,
            slug,
            warehouseId,
            description:
              typeof payload['description'] === 'string' ? payload['description'] : (existing?.description ?? null),
            spec: spec as DashboardSpec,
            saved: savedRef,
            patchSeq: nextPatchSeq,
            lastPersistedPatchSeq: existing?.lastPersistedPatchSeq ?? 0,
            externallyRemoved: false,
            pendingPersistPatchSeq,
          },
        };
      });

      const slugChanged = activeSlugRef.current !== null && activeSlugRef.current !== slug;
      const shouldAutoOpen = isNewTurn || slugChanged || !dismissedRef.current;
      if (isNewTurn || slugChanged) {
        dismissedRef.current = false;
      }
      if (shouldAutoOpen) {
        setActiveSlug(slug);
        setPanelOpen(true);
      } else {
        setActiveSlug((current) => current ?? slug);
      }
    },
    [commitArtifacts],
  );

  const setActive = useCallback((slug: string, options?: { openPanel?: boolean }) => {
    if (!artifactsRef.current[slug]) return;
    setActiveSlug(slug);
    if (options?.openPanel !== false) {
      dismissedRef.current = false;
      setPanelOpen(true);
    }
  }, []);

  const markSaved = useCallback(
    (slug: string, ref: SavedDashboardRef) => {
      commitArtifacts((prev) => {
        const existing = prev[slug];
        if (!existing) return prev;
        const sameRef = sameSavedRef(existing.saved, ref);
        if (sameRef && !isArtifactSaving(existing)) return prev;
        return {
          ...prev,
          [slug]: {
            ...existing,
            saved: ref,
            lastPersistedPatchSeq: existing.patchSeq,
            pendingPersistPatchSeq: undefined,
          },
        };
      });
    },
    [commitArtifacts],
  );

  const markAutoSaved = useCallback(
    (slug: string, seq: number) => {
      commitArtifacts((prev) => {
        const existing = prev[slug];
        if (!existing) return prev;
        return {
          ...prev,
          [slug]: {
            ...existing,
            lastPersistedPatchSeq: Math.max(existing.lastPersistedPatchSeq, seq),
          },
        };
      });
    },
    [commitArtifacts],
  );

  const syncAfterSave = useCallback(
    (slug: string, snapshot: DashboardArtifactSnapshot, savedRef: SavedDashboardRef, savedPatchSeq?: number) => {
      commitArtifacts((prev) => {
        const existing = prev[slug];
        const currentSeq = existing?.patchSeq ?? 0;
        const flushedSeq = savedPatchSeq ?? existing?.pendingPersistPatchSeq;

        // No flushed seq (pending cleared before late adopt): if the live draft is ahead of
        // the last ack, do not clobber it — mark saved and let auto-save catch up.
        // When live is still at lastPersisted (e.g. first save right after preview), apply
        // the snapshot as usual.
        if (flushedSeq === undefined && existing && existing.patchSeq > existing.lastPersistedPatchSeq) {
          return {
            ...prev,
            [slug]: {
              ...existing,
              saved: savedRef,
              description: snapshot.description !== undefined ? snapshot.description : (existing.description ?? null),
              pendingPersistPatchSeq: undefined,
              externallyRemoved: false,
            },
          };
        }

        const hasNewerDraft = existing !== undefined && flushedSeq !== undefined && existing.patchSeq > flushedSeq;

        if (hasNewerDraft && existing) {
          return {
            ...prev,
            [slug]: {
              ...existing,
              saved: savedRef,
              lastPersistedPatchSeq: Math.max(existing.lastPersistedPatchSeq, flushedSeq),
              pendingPersistPatchSeq: undefined,
              externallyRemoved: false,
            },
          };
        }

        const persistedSeq = flushedSeq ?? currentSeq;
        const resolvedSnapshot: DashboardArtifactSnapshot = {
          ...snapshot,
          description:
            snapshot.description !== undefined
              ? snapshot.description
              : (existing?.description ?? snapshot.description ?? null),
        };
        return {
          ...prev,
          [slug]: {
            ...snapshotToArtifact(
              resolvedSnapshot,
              savedRef,
              currentSeq,
              Math.max(existing?.lastPersistedPatchSeq ?? 0, persistedSeq),
            ),
            externallyRemoved: false,
            pendingPersistPatchSeq: undefined,
          },
        };
      });
      setActiveSlug((current) => current ?? slug);
    },
    [commitArtifacts],
  );

  const syncFromTranscript = useCallback(
    (snapshot: DashboardArtifactSnapshot) => {
      commitArtifacts((prev) => {
        const existing = prev[snapshot.slug];
        // Idempotent adopt: when the live store already holds this slug as an unsaved draft with
        // identical content (the SSE dashboard_draft stream applied it this turn, then the preview
        // tool_result adopts the same spec), don't bump patchSeq again. A phantom bump leaves
        // patchSeq > lastPersistedPatchSeq with no new content, which — once the slug is saved — makes
        // auto-save fire a redundant dashboard.save that can race an in-flight update_dashboard.
        if (
          existing &&
          isSameDraftContent(existing, snapshot) &&
          existing.saved === null &&
          !existing.externallyRemoved
        ) {
          return prev;
        }
        const nextPatchSeq = nextSessionPatchSeq(existing);
        return {
          ...prev,
          [snapshot.slug]: {
            ...snapshot,
            saved: null,
            patchSeq: nextPatchSeq,
            lastPersistedPatchSeq: existing?.lastPersistedPatchSeq ?? 0,
            externallyRemoved: false,
          },
        };
      });
      setActiveSlug((current) => current ?? snapshot.slug);
    },
    [commitArtifacts],
  );

  const markSavedRemoved = useCallback(
    (slug: string) => {
      commitArtifacts((prev) => {
        const existing = prev[slug];
        if (!existing) return prev;
        return {
          ...prev,
          [slug]: {
            ...existing,
            saved: null,
            externallyRemoved: true,
          },
        };
      });
    },
    [commitArtifacts],
  );

  const restoreDraftFromMemory = useCallback(
    (slug: string) => {
      commitArtifacts((prev) => {
        const existing = prev[slug];
        if (!existing || !canRestoreDashboardDraft(existing)) return prev;
        return {
          ...prev,
          [slug]: {
            ...existing,
            saved: null,
            externallyRemoved: false,
          },
        };
      });
    },
    [commitArtifacts],
  );

  const hydrateSavedAndOpen = useCallback(
    (slug: string, hint: { title: string; dashboardId?: string }) => {
      const savedRef: SavedDashboardRef | null =
        hint.dashboardId && hint.dashboardId.length > 0 ? { id: hint.dashboardId, slug } : null;
      const emptySpec: DashboardSpec = { parameters: [], panels: [] };

      commitArtifacts((prev) => {
        const existing = prev[slug];
        return {
          ...prev,
          [slug]: {
            title: hint.title,
            slug,
            warehouseId: existing?.warehouseId ?? '',
            description: existing?.description ?? null,
            spec: existing?.spec ?? emptySpec,
            saved: savedRef ?? existing?.saved ?? null,
            patchSeq: existing?.patchSeq ?? 0,
            lastPersistedPatchSeq: existing?.lastPersistedPatchSeq ?? 0,
            externallyRemoved: false,
            pendingPersistPatchSeq: undefined,
          },
        };
      });

      dismissedRef.current = false;
      setActiveSlug(slug);
      setPanelOpen(true);
    },
    [commitArtifacts],
  );

  const hydrateNotFoundAndOpen = useCallback(
    (slug: string, hint: { title: string }) => {
      const emptySpec: DashboardSpec = { parameters: [], panels: [] };

      commitArtifacts((prev) => {
        const existing = prev[slug];
        return {
          ...prev,
          [slug]: {
            title: hint.title,
            slug,
            warehouseId: existing?.warehouseId ?? '',
            description: existing?.description ?? null,
            spec: existing?.spec ?? emptySpec,
            saved: null,
            patchSeq: existing?.patchSeq ?? 0,
            lastPersistedPatchSeq: existing?.lastPersistedPatchSeq ?? 0,
            externallyRemoved: true,
            pendingPersistPatchSeq: undefined,
          },
        };
      });

      dismissedRef.current = false;
      setActiveSlug(slug);
      setPanelOpen(true);
    },
    [commitArtifacts],
  );

  const syncMetaFromDb = useCallback(
    (slug: string, meta: { title: string; description: string | null }) => {
      commitArtifacts((prev) => {
        const existing = prev[slug];
        if (!existing) return prev;
        if (existing.title === meta.title && (existing.description ?? null) === meta.description) return prev;
        return {
          ...prev,
          [slug]: { ...existing, title: meta.title, description: meta.description },
        };
      });
    },
    [commitArtifacts],
  );

  const clearServerPersistPending = useCallback(
    (slug: string) => {
      commitArtifacts((prev) => {
        const existing = prev[slug];
        if (existing?.pendingPersistPatchSeq === undefined) return prev;
        return {
          ...prev,
          [slug]: { ...existing, pendingPersistPatchSeq: undefined },
        };
      });
    },
    [commitArtifacts],
  );

  const clearAllServerPersistPending = useCallback(() => {
    commitArtifacts((prev) => {
      let changed = false;
      const next: Record<string, DashboardArtifact> = {};
      for (const [slug, artifact] of Object.entries(prev)) {
        // Clear the in-flight marker so auto-save resumes after a terminal abort (see interface doc).
        if (artifact.pendingPersistPatchSeq !== undefined) {
          next[slug] = { ...artifact, pendingPersistPatchSeq: undefined };
          changed = true;
        } else {
          next[slug] = artifact;
        }
      }
      return changed ? next : prev;
    });
  }, [commitArtifacts]);

  const restoreServerPersistPending = useCallback(
    (pending: Record<string, number>) => {
      const entries = Object.entries(pending);
      if (entries.length === 0) return;
      commitArtifacts((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const [slug, seq] of entries) {
          const existing = prev[slug];
          // Restore only when the marker is still cleared (don't clobber a newer in-flight update) AND the
          // artifact is a saved one with unpersisted edits. If the update already adopted, syncAfterSave
          // advanced lastPersistedPatchSeq past `seq`, so this guard is false and we don't re-wedge it.
          if (
            existing &&
            existing.pendingPersistPatchSeq === undefined &&
            existing.saved !== null &&
            existing.patchSeq > existing.lastPersistedPatchSeq
          ) {
            next[slug] = { ...existing, pendingPersistPatchSeq: seq };
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    },
    [commitArtifacts],
  );

  const restoreFromHistory = useCallback(
    (entries: Record<string, DashboardArtifact>, options?: { hasActiveTurn?: boolean }) => {
      const hasActiveTurn = options?.hasActiveTurn ?? false;
      const liveOnlySlugs = Object.keys(artifactsRef.current).filter((slug) => !(slug in entries));

      commitArtifacts((prev) => {
        const merged = { ...entries };
        for (const [slug, live] of Object.entries(prev)) {
          const restored = merged[slug];
          if (!restored) {
            merged[slug] = live;
            continue;
          }
          if (live.patchSeq > restored.patchSeq) {
            merged[slug] = live;
            continue;
          }
          if (live.patchSeq < restored.patchSeq) {
            continue;
          }
          // Same seq: prefer restored when live is an empty hydrate shell.
          if (isArtifactContentEmpty(live) && !isArtifactContentEmpty(restored)) {
            continue;
          }
          merged[slug] = live;
        }
        artifactsRef.current = merged;
        return merged;
      });

      if (liveOnlySlugs.length > 0) {
        // An in-flight preview survived the merge → auto-open it.
        const activeSlug = activeSlugRef.current;
        if (activeSlug && artifactsRef.current[activeSlug]) {
          setActiveSlug(activeSlug);
          setPanelOpen(true);
          dismissedRef.current = false;
        }
      } else if (!hasActiveTurn) {
        // Settled reload, nothing in-flight → don't auto-open a panel on reload.
        setActiveSlug(null);
        setPanelOpen(false);
        dismissedRef.current = true;
      }
      // else (mid-turn restore, no live-only slug): leave the panel exactly as the user has it — don't
      // yank a watched panel closed, don't reopen a dismissed one.

      if (!hasActiveTurn) {
        // Only reset the turn/dedup refs on a settled restore. Doing it mid-turn would make the next
        // same-turn draft look like a new turn — reopening a dismissed panel and re-arming the dedup guard.
        lastPatchSeqBySlugRef.current = new Map();
        lastPatchTurnRef.current = null;
      }
    },
    [commitArtifacts],
  );

  const snapshot = useCallback(() => {
    if (!activeSlug) return undefined;
    const artifact = artifacts[activeSlug];
    if (!artifact) return undefined;
    return toAgentDashboardSnapshot(artifact);
  }, [activeSlug, artifacts]);

  const inventorySnapshot = useCallback(
    () =>
      artifactList.map((artifact) => ({
        slug: artifact.slug,
        title: artifact.title,
        saved: artifact.saved !== null,
        ...(artifact.saved?.id ? { dashboardId: artifact.saved.id } : {}),
      })),
    [artifactList],
  );

  return useMemo(
    () => ({
      artifacts,
      artifactList,
      activeSlug,
      activeArtifact,
      panelOpen,
      closePanel,
      discard,
      applyAgentDraft,
      setActive,
      markSaved,
      markAutoSaved,
      syncAfterSave,
      syncFromTranscript,
      syncMetaFromDb,
      clearServerPersistPending,
      clearAllServerPersistPending,
      restoreServerPersistPending,
      markSavedRemoved,
      restoreDraftFromMemory,
      hydrateSavedAndOpen,
      hydrateNotFoundAndOpen,
      restoreFromHistory,
      snapshot,
      inventorySnapshot,
    }),
    [
      artifacts,
      artifactList,
      activeSlug,
      activeArtifact,
      panelOpen,
      closePanel,
      discard,
      applyAgentDraft,
      setActive,
      markSaved,
      markAutoSaved,
      syncAfterSave,
      syncFromTranscript,
      syncMetaFromDb,
      clearServerPersistPending,
      clearAllServerPersistPending,
      restoreServerPersistPending,
      markSavedRemoved,
      restoreDraftFromMemory,
      hydrateSavedAndOpen,
      hydrateNotFoundAndOpen,
      restoreFromHistory,
      snapshot,
      inventorySnapshot,
    ],
  );
}
